import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileText, PencilSimple, Eye, ArrowCounterClockwise, FloppyDisk, Gear } from "@phosphor-icons/react";
import CclBody from "@/components/app/ccl/CclBody";
import CclEditor from "@/components/app/ccl/CclEditor";

const MATTER_LABELS = { purchase: "Purchase", sale: "Sale", remortgage: "Remortgage", transfer: "Transfer of Equity" };

/**
 * Solicitor view + edit of the firm's Client Care Letter, in the case
 * Onboarding tab. Three tabs: Preview this case, Edit template (per
 * matter type, with live unsaved preview) and Letter settings (firm
 * details + client-account bank details).
 */
export default function CclLetterCard({ caseId }) {
  const [tab, setTab] = useState("preview");
  const [open, setOpen] = useState(false);

  // Preview
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewBody, setPreviewBody] = useState("");
  const [draftPreview, setDraftPreview] = useState(false);

  // Edit template
  const [matterType, setMatterType] = useState("purchase");
  const [matterTypes, setMatterTypes] = useState(["purchase", "sale", "remortgage", "transfer"]);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplBody, setTplBody] = useState("");
  const [savedBody, setSavedBody] = useState("");
  const [mergeTags, setMergeTags] = useState([]);
  const [isCustom, setIsCustom] = useState(false);
  const [saving, setSaving] = useState(false);

  // Settings
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Per-case override
  const [ovLoading, setOvLoading] = useState(false);
  const [ovBody, setOvBody] = useState("");
  const [ovSaved, setOvSaved] = useState("");
  const [hasOverride, setHasOverride] = useState(false);
  const [savingOv, setSavingOv] = useState(false);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true); setDraftPreview(false);
    try {
      const r = await api.get(`/onboarding/cases/${caseId}/ccl`);
      const forms = r.data?.forms || [];
      const ccl = forms.find((f) => f.form === "ccl") || forms[0];
      setPreviewBody(ccl?.body || r.data?.body || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load the letter");
    } finally { setPreviewLoading(false); }
  }, [caseId]);

  const loadTemplate = useCallback(async (mt) => {
    setTplLoading(true);
    try {
      const r = await api.get(`/onboarding/ccl-template`, { params: { matter_type: mt } });
      setTplBody(r.data.body || "");
      setSavedBody(r.data.body || "");
      setMergeTags(r.data.merge_tags || []);
      setIsCustom(!!r.data.is_custom);
      setMatterTypes(r.data.matter_types || matterTypes);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load the template");
    } finally { setTplLoading(false); }
  }, [matterTypes]);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const r = await api.get(`/onboarding/ccl-settings`);
      setSettings(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load settings");
    } finally { setSettingsLoading(false); }
  }, []);

  const loadOverride = useCallback(async () => {
    setOvLoading(true);
    try {
      const r = await api.get(`/onboarding/cases/${caseId}/ccl-override`);
      setOvBody(r.data.body || "");
      setOvSaved(r.data.has_override ? (r.data.body || "") : "");
      setHasOverride(!!r.data.has_override);
      setMergeTags(r.data.merge_tags || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load the letter");
    } finally { setOvLoading(false); }
  }, [caseId]);

  useEffect(() => {
    if (!open) return;
    if (tab === "preview" && !draftPreview) loadPreview();
    else if (tab === "edit") loadTemplate(matterType);
    else if (tab === "case") loadOverride();
    else if (tab === "settings") loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const changeMatter = async (mt) => { setMatterType(mt); await loadTemplate(mt); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/onboarding/ccl-template`, { body: tplBody, matter_type: matterType });
      setSavedBody(r.data.body || ""); setTplBody(r.data.body || ""); setIsCustom(!!r.data.is_custom);
      toast.success(`${MATTER_LABELS[matterType]} letter saved`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    setSaving(true);
    try {
      await api.put(`/onboarding/ccl-template`, { body: "", matter_type: matterType });
      await loadTemplate(matterType);
      toast.success(`${MATTER_LABELS[matterType]} reset to the default UKC letter`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed");
    } finally { setSaving(false); }
  };

  const previewUnsaved = async () => {
    try {
      const r = await api.post(`/onboarding/cases/${caseId}/ccl/preview`, { body: tplBody, matter_type: matterType });
      setPreviewBody(r.data.body || ""); setDraftPreview(true); setTab("preview");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await api.put(`/onboarding/ccl-settings`, settings);
      setSettings(r.data);
      toast.success("Letter settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSavingSettings(false); }
  };

  const saveOverride = async () => {
    setSavingOv(true);
    try {
      const r = await api.put(`/onboarding/cases/${caseId}/ccl-override`, { body: ovBody });
      setHasOverride(!!r.data.has_override);
      setOvSaved(r.data.has_override ? ovBody : "");
      toast.success("This case's letter saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSavingOv(false); }
  };

  const revertOverride = async () => {
    setSavingOv(true);
    try {
      await api.put(`/onboarding/cases/${caseId}/ccl-override`, { body: "" });
      await loadOverride();
      toast.success("Reverted to the firm master letter");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Revert failed");
    } finally { setSavingOv(false); }
  };

  const previewUnsavedOverride = async () => {
    try {
      const r = await api.post(`/onboarding/cases/${caseId}/ccl/preview`, { body: ovBody });
      setPreviewBody(r.data.body || ""); setDraftPreview(true); setTab("preview");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    }
  };

  const dirty = tplBody !== savedBody;
  const ovDirty = ovBody !== (hasOverride ? ovSaved : "") && ovBody.trim() !== "";
  const setField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const TabBtn = ({ id, icon: Icon, children, testid }) => (
    <button onClick={() => setTab(id)}
      className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 ${
        tab === id ? "bg-[#100f5d] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
      data-testid={testid}>
      <Icon weight="bold" className="w-3.5 h-3.5" /> {children}
    </button>
  );

  const settingsFields = [
    ["supervising_solicitor", "Supervising solicitor"],
    ["firm_address", "Firm address (comma-separated lines)"],
    ["firm_phone", "Firm phone"],
    ["client_account_name", "Client account name"],
    ["client_account_sort_code", "Sort code"],
    ["client_account_number", "Account number"],
    ["on_account_purchase", "Money on account — Purchase (£)"],
    ["on_account_sale", "Money on account — Sale (£)"],
    ["on_account_remortgage", "Money on account — Remortgage (£)"],
    ["on_account_transfer", "Money on account — Transfer (£)"],
  ];

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="ccl-letter-card">
      <button onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid="ccl-letter-toggle">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <FileText weight="bold" className="w-4 h-4 text-[#100f5d]" />
          Client Care Letter
          <span className="text-[11px] font-normal text-slate-500">— view & edit the letter your client signs</span>
        </span>
        <span className="text-xs text-[#100f5d]">{open ? "Hide" : "Open"}</span>
      </button>

      {open && (
        <div className="border-t border-slate-100 p-4">
          <div className="flex items-center gap-1 mb-3 flex-wrap">
            <TabBtn id="preview" icon={Eye} testid="ccl-tab-preview">Preview this case</TabBtn>
            <TabBtn id="case" icon={PencilSimple} testid="ccl-tab-case">Edit this case</TabBtn>
            <TabBtn id="edit" icon={PencilSimple} testid="ccl-tab-edit">Edit template</TabBtn>
            <TabBtn id="settings" icon={Gear} testid="ccl-tab-settings">Letter settings</TabBtn>
          </div>

          {tab === "preview" && (
            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-4 max-h-[460px] overflow-y-auto" data-testid="ccl-preview-render">
              {draftPreview && (
                <div className="mb-2 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block">
                  Showing UNSAVED draft ({MATTER_LABELS[matterType]}) · <button className="underline" onClick={loadPreview} data-testid="ccl-preview-saved">show saved</button>
                </div>
              )}
              {previewLoading
                ? <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
                : <CclBody text={previewBody} className="font-sans text-[13px] leading-relaxed text-slate-800" />}
              <p className="mt-3 text-[11px] text-slate-400 border-t border-slate-200 pt-2">
                The firm header (logo + address) and regulatory footer are added automatically on the signed PDF.
              </p>
            </div>
          )}

          {tab === "edit" && (
            <div data-testid="ccl-edit-panel">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-500">Matter type:</span>
                  <select value={matterType} onChange={(e) => changeMatter(e.target.value)}
                    className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs" data-testid="ccl-matter-select">
                    {matterTypes.map((m) => <option key={m} value={m}>{MATTER_LABELS[m] || m}</option>)}
                  </select>
                  <span className="text-[11px] text-slate-400">{isCustom ? "customised" : "UKC default"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={previewUnsaved} className="text-[11px] text-[#100f5d] hover:underline flex items-center gap-1" data-testid="ccl-preview-unsaved-btn">
                    <Eye weight="bold" className="w-3.5 h-3.5" /> Preview unsaved
                  </button>
                  <button onClick={resetToDefault} disabled={saving}
                    className="text-[11px] text-slate-500 hover:text-slate-700 flex items-center gap-1 disabled:opacity-50" data-testid="ccl-reset-btn">
                    <ArrowCounterClockwise weight="bold" className="w-3.5 h-3.5" /> Reset
                  </button>
                  <button onClick={save} disabled={saving || !dirty}
                    className="text-xs px-3 py-1.5 rounded-md bg-[#100f5d] text-white flex items-center gap-1 hover:bg-[#0b0a3d] disabled:opacity-50" data-testid="ccl-save-btn">
                    <FloppyDisk weight="bold" className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
              <CclEditor value={tplBody} onChange={setTplBody} disabled={tplLoading} testid="ccl-edit-textarea" />
              {mergeTags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ccl-merge-tags">
                  <span className="text-[11px] text-slate-500 mr-1">Merge tags:</span>
                  {mergeTags.map((t) => <code key={t} className="text-[10px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">{t}</code>)}
                </div>
              )}
            </div>
          )}

          {tab === "case" && (
            <div data-testid="ccl-case-panel">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <p className="text-[11px] text-slate-500">
                  Tweaks apply to <strong>this client only</strong>, on top of the firm master.
                  {hasOverride
                    ? <span className="ml-1 text-amber-700 font-medium">This case has a custom letter.</span>
                    : <span className="ml-1">Currently using the firm master.</span>}
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={previewUnsavedOverride} className="text-[11px] text-[#100f5d] hover:underline flex items-center gap-1" data-testid="ccl-case-preview-btn">
                    <Eye weight="bold" className="w-3.5 h-3.5" /> Preview unsaved
                  </button>
                  <button onClick={revertOverride} disabled={savingOv || !hasOverride}
                    className="text-[11px] text-slate-500 hover:text-slate-700 flex items-center gap-1 disabled:opacity-40" data-testid="ccl-case-revert-btn">
                    <ArrowCounterClockwise weight="bold" className="w-3.5 h-3.5" /> Revert to master
                  </button>
                  <button onClick={saveOverride} disabled={savingOv || !ovDirty}
                    className="text-xs px-3 py-1.5 rounded-md bg-[#100f5d] text-white flex items-center gap-1 hover:bg-[#0b0a3d] disabled:opacity-50" data-testid="ccl-case-save-btn">
                    <FloppyDisk weight="bold" className="w-3.5 h-3.5" /> {savingOv ? "Saving…" : "Save for this case"}
                  </button>
                </div>
              </div>
              <CclEditor value={ovBody} onChange={setOvBody} disabled={ovLoading} testid="ccl-case-textarea" />
              {mergeTags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span className="text-[11px] text-slate-500 mr-1">Merge tags:</span>
                  {mergeTags.map((t) => <code key={t} className="text-[10px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">{t}</code>)}
                </div>
              )}
            </div>
          )}

          {tab === "settings" && (
            <div data-testid="ccl-settings-panel">
              {settingsLoading || !settings ? (
                <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
              ) : (
                <>
                  <p className="text-[11px] text-slate-500 mb-3">These appear on every Client Care Letter (supervisor, firm header address, and the client-account details in the payment section).</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {settingsFields.map(([k, label]) => (
                      <label key={k} className="block">
                        <span className="block text-[11px] font-semibold text-slate-600 mb-1">{label}</span>
                        <input value={settings[k] || ""} onChange={(e) => setField(k, e.target.value)}
                          className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#100f5d]/30"
                          data-testid={`ccl-setting-${k}`} />
                      </label>
                    ))}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button onClick={saveSettings} disabled={savingSettings}
                      className="text-xs px-3 py-1.5 rounded-md bg-[#100f5d] text-white flex items-center gap-1 hover:bg-[#0b0a3d] disabled:opacity-50" data-testid="ccl-settings-save-btn">
                      <FloppyDisk weight="bold" className="w-3.5 h-3.5" /> {savingSettings ? "Saving…" : "Save settings"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
